import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Chip,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import {
  getActiveProjectId,
  getActiveProjectName,
  listProjects,
  switchProject,
} from "src/handlers/Projects";

// Sidebar project switcher.
//
// Reads the active project ID/name from `sessionStorage` (via the Projects
// handler). On click, fetches the recent projects for the signed-in user and
// shows them in a `<Menu>`. Picking a project calls `switchProject` which
// broadcasts `projectSwitched` so the rest of the app reloads its data.
//
// If the user is signed out or has no projects, the component collapses to a
// subtle "Sign in to sync" placeholder.

const MAX_RECENT_PROJECTS = 6;

function getSignedInState() {
  try {
    return Boolean(localStorage.getItem("sessioninfo"));
  } catch {
    return false;
  }
}

export default function ProjectSwitcher({ onNavigate }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState(() => getActiveProjectId());
  const [activeProjectName, setActiveProjectName] = useState(() => getActiveProjectName());
  const [isSignedIn, setIsSignedIn] = useState(getSignedInState);

  // React to project/session changes from other tabs or handlers.
  useEffect(() => {
    const refresh = () => {
      setActiveProjectId(getActiveProjectId());
      setActiveProjectName(getActiveProjectName());
      setIsSignedIn(getSignedInState());
    };
    window.addEventListener("storage", refresh);
    window.addEventListener("projectSwitched", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("projectSwitched", refresh);
    };
  }, []);

  const fetchProjects = useCallback(async () => {
    if (!isSignedIn) return;
    setLoading(true);
    try {
      const rows = await listProjects();
      setProjects(rows.slice(0, MAX_RECENT_PROJECTS));
    } catch (error) {
      console.error("ProjectSwitcher: failed to list projects", error);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    fetchProjects();
  }, [fetchProjects]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleSwitch = useCallback(
    async (project) => {
      handleClose();
      if (!project?.$id || project.$id === activeProjectId) return;
      try {
        await switchProject(project);
        setActiveProjectId(project.$id);
        setActiveProjectName(project.name || "");
        onNavigate?.();
      } catch (error) {
        console.error("ProjectSwitcher: failed to switch", error);
      }
    },
    [activeProjectId, handleClose, onNavigate],
  );

  const currentProject = useMemo(
    () =>
      projects.find((project) => project.$id === activeProjectId) || {
        $id: activeProjectId,
        name: activeProjectName,
      },
    [activeProjectId, activeProjectName, projects],
  );

  // "Local draft" indicator: shown both when not signed in AND when signed
  // in but with no active project. In both cases the user's edits live in
  // browser storage only; the dashed border + grey palette are intentional
  // visual cues that this state is non-cloud-backed.
  if (!isSignedIn || !activeProjectId) {
    return (
      <Tooltip
        title={
          isSignedIn
            ? "You don't have an active project. Edits are kept locally until you create or switch to one."
            : "You're working locally. Sign in to sync this project to the cloud and share it."
        }
        placement="bottom"
        arrow
      >
        <Chip
          icon={<FolderSpecialRoundedIcon fontSize="small" />}
          label="Local draft"
          variant="outlined"
          sx={(theme) => ({
            maxWidth: 240,
            height: 34,
            px: 0.75,
            fontWeight: 700,
            borderWidth: 1.5,
            borderStyle: "dashed",
            borderColor:
              theme.palette.mode === "light"
                ? "rgba(148, 163, 184, 0.55)"
                : "rgba(148, 163, 184, 0.4)",
            color: theme.palette.text.secondary,
            "& .MuiChip-icon": { ml: 0.75, color: "inherit" },
            "& .MuiChip-label": { px: 1 },
          })}
        />
      </Tooltip>
    );
  }

  return (
    <>
      <Chip
        ref={anchorRef}
        onClick={handleOpen}
        icon={<FolderSpecialRoundedIcon fontSize="small" />}
        deleteIcon={<ArrowDropDownRoundedIcon fontSize="small" />}
        onDelete={handleOpen}
        label={currentProject?.name || "Select a project"}
        title={currentProject?.name || undefined}
        color="success"
        variant="outlined"
        aria-haspopup="menu"
        aria-expanded={open || undefined}
        sx={(theme) => ({
          maxWidth: 240,
          height: 34,
          px: 0.75,
          fontWeight: 700,
          borderWidth: 1.5,
          cursor: "pointer",
          borderColor:
            theme.palette.mode === "light"
              ? "rgba(22, 163, 74, 0.28)"
              : "rgba(134, 239, 172, 0.26)",
          backgroundColor:
            theme.palette.mode === "light"
              ? "rgba(240, 253, 244, 0.9)"
              : "rgba(20, 83, 45, 0.22)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "inset 0 0 0 1px rgba(134, 239, 172, 0.06)"
              : "0 1px 0 rgba(255, 255, 255, 0.55)",
          "& .MuiChip-icon": {
            ml: 0.75,
            color: theme.palette.mode === "light" ? "#15803d" : "#86efac",
          },
          "& .MuiChip-label": {
            px: 1,
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
        })}
      />

      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ sx: { minWidth: 260, maxWidth: 340 } }}
      >
        <Box
          sx={{
            px: 1.25,
            pt: 0.75,
            pb: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, fontSize: 10.5 }}
          >
            Recent projects
          </Typography>
          {!loading && projects.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {projects.length}
            </Typography>
          ) : null}
        </Box>

        {loading ? (
          <Box sx={{ px: 1, py: 0.5 }}>
            <Stack spacing={0.5}>
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
            </Stack>
          </Box>
        ) : projects.length ? (
          projects.map((project) => {
            const active = project.$id === activeProjectId;
            return (
              <MenuItem
                key={project.$id}
                onClick={() => handleSwitch(project)}
                selected={active}
              >
                <FolderSpecialRoundedIcon
                  fontSize="small"
                  sx={{
                    color: active ? "success.main" : "text.secondary",
                    opacity: active ? 1 : 0.7,
                  }}
                />
                <ListItemText
                  primary={project.name || "Untitled project"}
                  primaryTypographyProps={{
                    sx: {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                {active ? (
                  <CheckRoundedIcon fontSize="small" sx={{ color: "success.main" }} />
                ) : null}
              </MenuItem>
            );
          })
        ) : (
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <Typography variant="body2" color="text.secondary">
              You have no projects yet.
            </Typography>
          </Box>
        )}

        <Divider />
        <MenuItem
          component={RouterLink}
          to="/projects"
          onClick={() => {
            handleClose();
            onNavigate?.();
          }}
        >
          <OpenInNewRoundedIcon fontSize="small" sx={{ opacity: 0.7 }} />
          <ListItemText primary="All projects" />
        </MenuItem>
      </Menu>
    </>
  );
}
